import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { getNewsroomReturnTo, newsroomDetailState } from "@/lib/newsroomNavigation";
import { api } from "@/lib/api";
import { isLikelyHtml, resolveHtmlAssetUrls, sanitizeHtml } from "@/lib/html";
import { normalizeYouTubePosition } from "@/lib/youtube";
import type { TodayDetail, NewsDetail, CommunityDetail, PrevNext, YouTubePosition } from "@/lib/types";
import ScrollReveal from "@/components/base/ScrollReveal";
import YouTubeEmbed from "@/components/base/YouTubeEmbed";

type PostType = "today" | "news" | "community";

type NormalizedPost = {
  id: number;
  type: PostType;
  categoryLabel: string;
  categoryColor: string;
  title: string;
  author: string;
  date: string;
  views: number | null;
  link: string | null;
  youtubeUrl: string | null;
  youtubePosition: YouTubePosition;
  content: string | null;
  excerpt: string | null;
};

const categoryMeta: Record<PostType, { label: string; color: string }> = {
  today: {
    label: "Biocon Today",
    color: "bg-emerald-50 text-emerald-600",
  },
  news: {
    label: "Biocon News",
    color: "bg-sky-50 text-sky-600",
  },
  community: {
    label: "Community",
    color: "bg-amber-50 text-amber-700",
  },
};

export default function NewsroomDetailPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getNewsroomReturnTo(location.state);
  const detailLinkState = returnTo ? newsroomDetailState(returnTo) : undefined;

  const [post, setPost] = useState<NormalizedPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [prevPost, setPrevPost] = useState<PrevNext>(null);
  const [nextPost, setNextPost] = useState<PrevNext>(null);

  const validType = (type === "today" || type === "news" || type === "community") ? type as PostType : null;

  const load = useCallback(async () => {
    if (!validType || !id) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    try {
      const meta = categoryMeta[validType];

      if (validType === "today") {
        const res = await api.get<TodayDetail>(`/public/news/today/${id}`);
        const { current, prev, next } = res.data;
        setPost({
          id: current.id, type: "today",
          categoryLabel: meta.label, categoryColor: meta.color,
          title: current.title, author: "BIOCON", date: current.date,
          views: null, link: current.link,
          youtubeUrl: current.youtubeUrl, youtubePosition: normalizeYouTubePosition(current.youtubePosition),
          content: current.content, excerpt: null,
        });
        setPrevPost(prev);
        setNextPost(next);
      } else if (validType === "news") {
        const res = await api.get<NewsDetail>(`/public/news/news/${id}`);
        const { current, prev, next } = res.data;
        setPost({
          id: current.id, type: "news",
          categoryLabel: meta.label, categoryColor: meta.color,
          title: current.title, author: current.author || "BIOCON", date: current.date,
          views: null, link: current.link,
          youtubeUrl: current.youtubeUrl, youtubePosition: normalizeYouTubePosition(current.youtubePosition),
          content: current.content, excerpt: current.excerpt,
        });
        setPrevPost(prev);
        setNextPost(next);
      } else {
        const res = await api.get<CommunityDetail>(`/public/news/community/${id}`);
        const { current, prev, next } = res.data;
        // Increment views
        api.patch(`/public/news/community/${id}/views`).catch(() => {});
        setPost({
          id: current.id, type: "community",
          categoryLabel: meta.label, categoryColor: meta.color,
          title: current.title, author: current.author || "BIOCON", date: current.date,
          views: current.views, link: current.link,
          youtubeUrl: current.youtubeUrl, youtubePosition: normalizeYouTubePosition(current.youtubePosition),
          content: current.content, excerpt: null,
        });
        setPrevPost(prev);
        setNextPost(next);
      }
      setNotFound(false);
    } catch {
      setNotFound(true);
    }
    setLoading(false);
  }, [validType, id]);

  useEffect(() => { load(); }, [load]);

  const handleBack = () => {
    if (returnTo) navigate(returnTo);
    else if (window.history.length > 1) navigate(-1);
    else navigate("/newsroom");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-28 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1977cc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-white pt-28 flex flex-col items-center justify-center gap-4">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-50 text-gray-300">
          <i className="ri-file-list-line text-2xl" />
        </div>
        <p className="text-gray-400 text-sm">Post not found.</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-[#1977cc] text-white rounded-md text-sm font-medium hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
        >
          Back to Newsroom
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-[#f1f7fc] pt-28 pb-10">
        <div className="container mx-auto px-4">
          <ScrollReveal>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-6 bg-[#1977cc] rounded-full" />
              <h1 className="text-2xl md:text-3xl font-bold text-[#2c4964]">Newsroom</h1>
            </div>
            <nav className="text-sm text-gray-400 flex items-center gap-2 flex-wrap">
              <button onClick={handleBack} className="hover:text-[#1977cc] transition-colors cursor-pointer whitespace-nowrap">
                Newsroom
              </button>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="whitespace-nowrap">{post.categoryLabel}</span>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-[#2c4964] font-medium line-clamp-1">{post.title}</span>
            </nav>
          </ScrollReveal>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <ScrollReveal>
            <div className="mb-6">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap mb-4 ${post.categoryColor}`}>
                {post.categoryLabel}
              </span>
              <h2 className="text-xl md:text-2xl font-semibold text-[#2c4964] leading-snug mb-4">
                {post.title}
              </h2>
              <div className="flex items-center flex-wrap gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-user-line text-xs" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-calendar-line text-xs" />
                  {post.date}
                </span>
                {post.views !== null && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line text-xs" />
                    {post.views.toLocaleString()} views
                  </span>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-6 md:p-8">
              {post.youtubeUrl && post.youtubePosition === "top" && (
                <YouTubeEmbed url={post.youtubeUrl} title={post.title} className="mb-6" />
              )}
              {post.excerpt && (
                <p className="text-sm text-gray-500 leading-relaxed mb-6 pb-6 border-b border-gray-100 italic">
                  {post.excerpt}
                </p>
              )}
              {post.content ? (
                isLikelyHtml(post.content) ? (
                  <div
                    className="rich-content text-gray-700 leading-relaxed text-[15px]"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(resolveHtmlAssetUrls(post.content)) }}
                  />
                ) : (
                  <div className="text-gray-700 leading-relaxed text-[15px] whitespace-pre-line">
                    {post.content}
                  </div>
                )
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-50 text-gray-300">
                    <i className="ri-article-line text-xl" />
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    자세한 내용은 아래 원문 링크에서 확인하세요.
                  </p>
                </div>
              )}
              {post.youtubeUrl && post.youtubePosition === "bottom" && (
                <YouTubeEmbed url={post.youtubeUrl} title={post.title} className="mt-6" />
              )}
            </div>

            {post.link && (
              <div className="mt-6 flex items-center">
                <a
                  href={post.link}
                  target="_blank"
                  rel="nofollow"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1977cc]/8 text-[#1977cc] text-sm font-medium hover:bg-[#1977cc]/15 transition-colors"
                >
                  <i className="ri-external-link-line" />
                  원문 보기
                </a>
              </div>
            )}

            <div className="mt-10 border-t border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                <div className="py-5 sm:pr-6">
                  {prevPost ? (
                    <Link to={`/newsroom/${validType}/${prevPost.id}`} state={detailLinkState} className="group flex items-start gap-3 cursor-pointer">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 group-hover:border-[#1977cc] group-hover:text-[#1977cc] group-hover:bg-[#1977cc]/5 transition-all shrink-0 mt-0.5">
                        <i className="ri-arrow-left-s-line text-base" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 mb-1 whitespace-nowrap">이전 글</p>
                        <p className="text-sm font-medium text-[#2c4964] group-hover:text-[#1977cc] transition-colors line-clamp-2 leading-snug">
                          {prevPost.title}
                        </p>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-300 shrink-0">
                        <i className="ri-arrow-left-s-line text-base" />
                      </div>
                      <p className="text-xs text-gray-300">이전 글이 없습니다</p>
                    </div>
                  )}
                </div>
                <div className="py-5 sm:pl-6 sm:text-right">
                  {nextPost ? (
                    <Link to={`/newsroom/${validType}/${nextPost.id}`} state={detailLinkState} className="group flex items-start gap-3 sm:flex-row-reverse cursor-pointer">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 text-gray-400 group-hover:border-[#1977cc] group-hover:text-[#1977cc] group-hover:bg-[#1977cc]/5 transition-all shrink-0 mt-0.5">
                        <i className="ri-arrow-right-s-line text-base" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-400 mb-1 whitespace-nowrap">다음 글</p>
                        <p className="text-sm font-medium text-[#2c4964] group-hover:text-[#1977cc] transition-colors line-clamp-2 leading-snug">
                          {nextPost.title}
                        </p>
                      </div>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-3 sm:flex-row-reverse">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-100 text-gray-300 shrink-0">
                        <i className="ri-arrow-right-s-line text-base" />
                      </div>
                      <p className="text-xs text-gray-300">다음 글이 없습니다</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="pt-4 pb-2 flex justify-center">
                <button
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-gray-200 text-sm text-gray-500 hover:border-[#1977cc]/40 hover:text-[#1977cc] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-list-check text-xs" />
                  이전화면으로 돌아가기
                </button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </div>
  );
}
